import React from "react";
import { ShieldAlert, Ban, Info, RotateCcw } from "lucide-react";
import type { SowFields } from "../../../../../utils/api/endpoints/sow";

interface DisclaimersExclusionsEditorProps {
  fields: SowFields;
  setField: <K extends keyof SowFields>(key: K, value: SowFields[K]) => void;
}

export const DisclaimersExclusionsEditor: React.FC<DisclaimersExclusionsEditorProps> = ({
  fields,
  setField
}) => {
  const defaultDisclaimers = `Professional Conduct: CTB will perform the services with reasonable skill, care, and diligence, consistent with recognised industry practices applicable to penetration testing engagements.

Risk Acknowledgement: Testing will be conducted against a production environment. While CTB takes reasonable care to minimise disruption, certain testing activities carry an inherent risk of service degradation or unintended effects, and testing may generate records, notifications, state changes, or transactions within the Client's environment. The Client acknowledges these risks and is responsible for maintaining appropriate backups and operational safeguards during the testing window.

Issue Notification: CTB will promptly notify the Client of any material unintended issue arising from testing, and of any critical vulnerability identified during the engagement that warrants immediate attention.

Client Authority and Responsibility: The Client confirms that it has the authority to authorise testing of the in-scope targets and is responsible for obtaining any third-party authorisations required, including from hosting, infrastructure, payment, identity-verification, or other service providers, before testing begins.

Indemnity: To the extent permitted by law, the Client will indemnify CTB, its employees, and contractors against third-party claims arising from the Client's lack of authority to approve testing of the in-scope targets, except to the extent such claims are finally determined to result from CTB's wilful misconduct or gross negligence.

Confidentiality of Client Information: CTB acknowledges that the platform and the information disclosed during this engagement include commercially sensitive material, including material relating to intellectual property that has not yet been granted. Such information will be handled in accordance with the confidentiality obligations agreed between the parties and the incorporated CTB Customer Terms and Conditions, and disclosed only to CTB personnel engaged in the delivery of this engagement.

Data Handling and Retention: CTB will securely destroy all Client data and testing artefacts within 30 days of issuing the final penetration testing report, and will retain the report for no longer than 12 months unless the parties agree otherwise in writing. A certificate of destruction will be provided on request.

Limitation of Assurance: The assessment provides a point-in-time evaluation of the in-scope targets. It does not constitute a warranty that the targets are free of all vulnerabilities, and no assurance of completeness is given or implied. Security posture may change as the environment, configuration, or threat landscape evolves.

Governing Law and Jurisdiction: This Statement of Work is governed by and construed in accordance with the laws of New Zealand, and the parties submit to the exclusive jurisdiction of the New Zealand courts.`;

  const defaultStandardExclusions = `Any application, environment, domain, subdomain, API, service, or address not explicitly identified as an authorised target.
Internal networks, internal infrastructure, cloud infrastructure assessments, cloud configuration reviews, and underlying hosting platforms.
The systems and infrastructure of third-party payment processors, identity-verification providers, and other third-party service providers.
Testing of the payment path against live or billable transactions; all payment testing is limited to the Client's Stripe test environment (test mode).
Identity verification against live or billable services, and generation of live communications to real third-party recipients.
Denial-of-service, distributed denial-of-service, stress testing, load testing, and other availability-focused testing activities.
Physical security assessments, social engineering, phishing, vishing, and other human-focused security testing activities.
Source code review, secure code review, software composition analysis, architecture review, and threat modelling activities.
Formal compliance assessments, audits, certifications, or attestations against any regulatory or security framework.
Testing of functionality, features, or environments introduced after scope validation unless separately agreed in writing.`;

  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <ShieldAlert size={16} className="text-ctb-blue" />
          Section 7 & 8: Disclaimers, Conduct & Exclusions
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Review and customize the professional conduct standards, limitation of assurance, and explicit exclusions.
        </p>
      </div>

      {/* Disclaimers */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <Info size={13} className="text-slate-500" />
            7. Disclaimers & Professional Conduct
          </label>
          <button
            type="button"
            onClick={() => setField("disclaimersText", defaultDisclaimers)}
            className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RotateCcw size={11} />
            Reset to standard
          </button>
        </div>
        <textarea
          rows={7}
          value={fields.disclaimersText ?? ""}
          onChange={(e) => setField("disclaimersText", e.target.value)}
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
        />
      </div>

      {/* Standard Exclusions */}
      <div className="space-y-2 pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <Ban size={13} className="text-rose-600" />
            8. Standard Exclusions (Out of Scope)
          </label>
          <button
            type="button"
            onClick={() => setField("standardExclusions", defaultStandardExclusions)}
            className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RotateCcw size={11} />
            Reset to standard
          </button>
        </div>
        <textarea
          rows={6}
          value={fields.standardExclusions ?? ""}
          onChange={(e) => setField("standardExclusions", e.target.value)}
          placeholder="Enter each excluded activity on a new line..."
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
        />
      </div>

      {/* Additional Exclusions */}
      <div className="space-y-2 pt-2 border-t border-slate-200">
        <label className="text-xs font-bold text-slate-800">Additional / Engagement-Specific Exclusions</label>
        <textarea
          rows={3}
          value={fields.additionalExclusions || ""}
          onChange={(e) => setField("additionalExclusions", e.target.value)}
          placeholder="Any custom exclusions specific to this client's systems..."
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
        />
      </div>
    </div>
  );
};
